import React, { useEffect, useReducer, createContext, Dispatch } from "react";
import { AuthAction, AuthState } from "../../interfaces/Contexts";
import { ReactChildren } from "../../interfaces/Common";
import { AES, enc } from "crypto-js";
import { DATA_SALT, KEY_USER_DETAILS } from "../constant";
import KenLoader from "../../GlobalComponents/KenLoader";

export const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  search: null,
  loading: false,
};

const reducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "LOGIN":
      const userPayload = action.payload || { user: null };
      
      try {
        const data = AES.encrypt(JSON.stringify(userPayload), DATA_SALT).toString();
        localStorage.setItem(KEY_USER_DETAILS, data);
      } catch (error: any) {
        if (error.name === 'QuotaExceededError' || error.code === 22) {
          console.warn('localStorage quota exceeded. Storing minimal user data...');
          try {
            const { userDetails, ...userWithoutDetails } = userPayload.user || {};
            const minimalPayload = {
              user: userWithoutDetails,
              loading: userPayload.loading,
            };
            const minimalData = AES.encrypt(JSON.stringify(minimalPayload), DATA_SALT).toString();
            localStorage.setItem(KEY_USER_DETAILS, minimalData);
          } catch (retryError) {
            console.error('Failed to store minimal data:', retryError);
          }
        } else {
          console.error('Error storing data in localStorage:', error);
        }
      }
      
      return {
        ...state,
        loading: action.payload?.loading || state.loading,
        isAuthenticated: userPayload.user && userPayload.user === null ? false : true,
        user: userPayload.user || null,
      };
    case "LOGOUT":
      localStorage.clear();
      return {
        ...state,
        loading: true,
        isAuthenticated: false,
        user: null,
      };
    default:
      return state;
  }
};

export const AuthContext = createContext<{
  state: AuthState;
  dispatch: Dispatch<AuthAction>;
}>({ state: initialState, dispatch: () => {} });

export const AuthContextProvider: React.FunctionComponent<ReactChildren> = (props) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const fetchUserData = () => {
    const user = localStorage.getItem(KEY_USER_DETAILS);
    if (user) {
      try {
        const parseData = AES.decrypt(user, DATA_SALT).toString(enc.Utf8);
        const parsedUser: AuthState = JSON.parse(parseData);
        if (!parsedUser?.user) {
          dispatch({
            type: "LOGOUT",
          });
          return;
        }
        dispatch({
          type: "LOGIN",
          payload: {
            loading: true,
            user: parsedUser.user,
          },
        });
      } catch (error) {
        console.error('Error reading user data from localStorage:', error);
        localStorage.removeItem(KEY_USER_DETAILS);
        dispatch({
          type: "LOGOUT",
        });
      }
    } else {
      dispatch({
        type: "LOGOUT",
      });
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        state,
        dispatch,
      }}
    >
      {state.loading ? props.children : <KenLoader />}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): { state: AuthState; dispatch: Dispatch<AuthAction> } => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
